/**
 * Efeitos globais iniciados pelo App (`startEffects`; §2.2.3 passo 8, §10.1.2, §10.1.3; FE §6.8, DE-B03).
 *
 * Fase 3:
 * - **Tique único de 1 s → `now`** (`{ ms, tMin }` do relógio da FONTE, `source.clock()`): `setTimeout` reagendado a
 *   cada tique, alinhado ao segundo do aparelho (`1005 − Date.now() % 1000`), nunca `setInterval`. Com a aba oculta o
 *   tique para; em `visibilitychange` (visível) e em `pageshow`, recalcula na hora e reinicia (AC-10.1-04, AC-10.1-05).
 *   O relógio vem de um getter injetado pelo App (`() => fonteAtual.clock()`), para seguir a fonte nova depois de
 *   `recriar()` do DevPanel.
 * - **Relógio dos dados** (`timeline.clockTMin`): `instantTMin` do último snapshot vivo aceito; monotônico (nunca
 *   volta, nem com época nova). Ao vivo, `displayTMin` acompanha.
 *
 * Toast, overlay da TV, anúncios de evento (a LiveRegion já os faz a partir dos sinais) e o replay pedido pelo `t` da
 * URL entram nas fases donas (6, 8b, 10).
 */
import { effect } from '@preact/signals';
import { clockAt, epochMs, nextTickDelay } from '../model/time';
import { config, liveView, now, timeline } from './store';

/** Página: visibilidade e eventos de ciclo de vida (injetáveis nos testes). */
export interface EffectsDocument {
  readonly hidden: boolean;
  addEventListener(type: 'visibilitychange', fn: () => void): void;
  removeEventListener(type: 'visibilitychange', fn: () => void): void;
}
export interface EffectsWindow {
  addEventListener(type: 'pageshow', fn: () => void): void;
  removeEventListener(type: 'pageshow', fn: () => void): void;
}

export interface EffectsOptions {
  /** Relógio da fonte atual em ms (DE-B03). */
  clock: () => number;
  /** Padrão: `document`; null desliga a pausa com a aba oculta. */
  doc?: EffectsDocument | null;
  /** Padrão: `window`; null desliga o `pageshow`. */
  win?: EffectsWindow | null;
  /** Relógio do aparelho, só para alinhar o tique ao segundo (padrão: `Date.now`). */
  deviceNow?: () => number;
}

let stopCurrent: (() => void) | null = null;

/** Liga os efeitos (uma vez por documento; chamar de novo desliga os anteriores). Devolve o desligamento. */
export function startEffects(o: EffectsOptions): () => void {
  stopCurrent?.();
  const doc = o.doc === undefined ? (typeof document === 'undefined' ? null : document) : o.doc;
  const win = o.win === undefined ? (typeof window === 'undefined' ? null : window) : o.win;
  const deviceNow = o.deviceNow ?? Date.now;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const write = () => {
    const reading = clockAt(o.clock(), epochMs(config.peek().election_date));
    const prev = now.peek();
    if (prev.ms !== reading.ms || prev.tMin !== reading.tMin) now.value = reading;
  };
  const clear = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const tick = () => {
    timer = null;
    if (stopped || doc?.hidden) return;
    write();
    timer = setTimeout(tick, nextTickDelay(deviceNow()));
  };
  /** Volta da aba oculta, `pageshow` e a partida: lê na hora e reagenda um único temporizador. */
  const restart = () => {
    clear();
    tick();
  };
  const onVisibility = () => {
    if (doc?.hidden) clear();
    else restart();
  };

  doc?.addEventListener('visibilitychange', onVisibility);
  win?.addEventListener('pageshow', restart);
  restart();

  const disposeDataClock = effect(() => {
    const v = liveView.value;
    if (!v) return;
    const t = timeline.peek();
    if (!(v.instantTMin > t.clockTMin)) return;
    timeline.value = { ...t, clockTMin: v.instantTMin, displayTMin: t.live ? v.instantTMin : t.displayTMin };
  });

  const stop = () => {
    if (stopped) return;
    stopped = true;
    clear();
    doc?.removeEventListener('visibilitychange', onVisibility);
    win?.removeEventListener('pageshow', restart);
    disposeDataClock();
    if (stopCurrent === stop) stopCurrent = null;
  };
  stopCurrent = stop;
  return stop;
}

/**
 * Fonte nova pelo `recriar()` do DevPanel (outra noite simulada): o relógio dos dados recomeça, porque a monotonia de
 * §10.1.2 vale dentro de uma fonte; o 1º snapshot da fonte nova o regrava. Só a ligação do feed chama, ao recriar.
 */
export function resetDataClock(): void {
  const t = timeline.peek();
  if (t.clockTMin === 0) return;
  timeline.value = { ...t, clockTMin: 0, displayTMin: t.live ? 0 : t.displayTMin };
}
